import React, { useEffect, useState, useMemo } from 'react';
import { api, fmt, fmtDate } from '../utils/api';
import Topbar from '../components/Topbar';
import { EmptyState, Spinner, SearchInput, useToast, Modal } from '../components/UI';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function parseMonthDate(m) {
  if (!m) return 0;
  const p = String(m).split(' ');
  return parseInt(p[1]||0)*12 + MONTH_NAMES.indexOf(p[0]);
}

export default function Profit({ user, directorView }) {
  const [profitData, setProfitData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [personFilter, setPersonFilter] = useState('all');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [withdrawModal, setWithdrawModal] = useState(null);
  const [withdrawForm, setWithdrawForm] = useState({ amount:'', method:'Bank Transfer', date:'', notes:'' });
  const [editWithdrawModal, setEditWithdrawModal] = useState(null);
  const [editWithdrawForm, setEditWithdrawForm] = useState({ amount:'', method:'Bank Transfer', date:'', notes:'' });
  const [openingBalanceModal, setOpeningBalanceModal] = useState(null);
  const [obAmount, setObAmount] = useState('');
  const [obNotes, setObNotes] = useState('');
  const [obInfoModal, setObInfoModal] = useState(null);
  const [expandedPL, setExpandedPL] = useState({});
  const [expandedHist, setExpandedHist] = useState({});
  const [histYear, setHistYear] = useState(0);
  const [histSearch, setHistSearch] = useState('');
  const [plYear, setPlYear] = useState(new Date().getFullYear());
  const [expandedF, setExpandedF] = useState({});
  const [showToast, Toast] = useToast();
  const isAdmin = user?.role === 'admin' || user?.role === 'hq_support';
  const isDirector = user?.role === 'director';

  const load = () => {
    setLoading(true);
    Promise.all([
      api.getProfit(isAdmin || isDirector),
      api.getFranchises(directorView),
    ]).then(([profitResult, franchiseResult]) => {
      // Merge financials and salaries from franchises into profit data
      const franchiseMap = {};
      (franchiseResult||[]).forEach(f => { franchiseMap[f.id] = f; });
      const merged = (profitResult||[]).map(p => ({
        ...p,
        financials: franchiseMap[p.franchiseId]?.financials || [],
        salaries: franchiseMap[p.franchiseId]?.salaries || [],
      }));
      setProfitData(merged);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let data = profitData;
    if (search) {
      const q = search.toLowerCase();
      data = data.filter(f =>
        (f.franchiseName||'').toLowerCase().includes(q) ||
        [...(f.owners||[]), ...(f.investors||[])].some(p => (p.name||'').toLowerCase().includes(q))
      );
    }
    if (personFilter !== 'all') {
      data = data.filter(f =>
        [...(f.owners||[]),...(f.investors||[])].some(p=>p.name===personFilter)
      );
    }
    return data;
  }, [profitData, search, personFilter]);

  async function saveWithdrawal() {
    if (!withdrawForm.amount || !withdrawForm.date) { showToast('Please enter amount and date'); return; }
    await api.postWithdrawal({
      franchiseId: withdrawModal.franchiseId, userId: withdrawModal.userId,
      userEmail: withdrawModal.email, amount: parseFloat(withdrawForm.amount),
      method: withdrawForm.method, dateTaken: withdrawForm.date, notes: withdrawForm.notes||null,
    });
    await api.logActivity('Profit withdrawal', `${withdrawModal.name} — ${withdrawModal.franchiseName} — £${withdrawForm.amount}`);
    showToast('Withdrawal recorded'); setWithdrawModal(null); load();
  }

  async function saveEditWithdrawal() {
    await api.editWithdrawal(editWithdrawModal.id, editWithdrawForm);
    await api.logActivity('Edited withdrawal', `${editWithdrawModal.userName} — £${editWithdrawForm.amount}`);
    showToast('Updated'); setEditWithdrawModal(null); load();
  }

  async function deleteWithdrawal(w) {
    if (!window.confirm(`Delete withdrawal of £${w.amount} for ${w.userName}?`)) return;
    await api.deleteWithdrawal(w.id);
    await api.logActivity('Deleted withdrawal', `${w.userName} — £${w.amount}`);
    showToast('Deleted'); load();
  }

  async function saveOpeningBalance() {
    if (!obAmount) { showToast('Please enter an amount'); return; }
    await api.setOpeningBalance({ franchiseId: openingBalanceModal.franchiseId, userId: openingBalanceModal.userId, amount: parseFloat(obAmount), notes: obNotes||null });
    await api.logActivity('Set opening balance', `${openingBalanceModal.name} — ${openingBalanceModal.franchiseName} — £${obAmount}`);
    showToast('Opening balance set'); setOpeningBalanceModal(null); load();
  }

  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="Profit" /><Spinner /></div>;

  const totalOwed = profitData.reduce((s,f) => s+[...(f.owners||[]),...(f.investors||[])].reduce((ss,p)=>ss+(parseFloat(p.totalOwed)||0),0),0);
  const totalTaken = profitData.reduce((s,f) => s+[...(f.owners||[]),...(f.investors||[])].reduce((ss,p)=>ss+(parseFloat(p.totalTaken)||0),0),0);

  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title="Profit" subtitle="Franchisee & investor profit balances" />
      {Toast}
      <div style={{ padding:24 }}>
        {/* Summary */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:20 }}>
          <div className="card" style={{ padding:'14px 16px' }}>
            <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:4 }}>Total Owed</div>
            <div style={{ fontSize:22, fontWeight:700, color:'var(--navy)' }}>{fmt(totalOwed)}</div>
          </div>
          <div className="card" style={{ padding:'14px 16px' }}>
            <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:4 }}>Total Taken</div>
            <div style={{ fontSize:22, fontWeight:700, color:'var(--text-secondary)' }}>{fmt(totalTaken)}</div>
          </div>
          <div className="card" style={{ padding:'14px 16px' }}>
            <div style={{ fontSize:11, color:'var(--text-tertiary)', marginBottom:4 }}>Total Remaining</div>
            <div style={{ fontSize:22, fontWeight:700, color:'var(--green)' }}>{fmt(totalOwed-totalTaken)}</div>
          </div>
        </div>

        <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
          <div style={{ flex:1, maxWidth:320 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search by branch or person..." />
          </div>
          {/* Person filter */}
          <select value={personFilter} onChange={e=>setPersonFilter(e.target.value)} style={{ width:'auto', marginBottom:0 }}>
            <option value="all">All People</option>
            {[...new Set(profitData.flatMap(f=>[...(f.owners||[]),...(f.investors||[])].map(p=>p.name)).filter(Boolean))].sort().map(n=><option key={n} value={n}>{n}</option>)}
          </select>
        </div>

        {/* Franchise cards */}
        {filtered.map(f => {
          const allPeople = [...(f.owners||[]), ...(f.investors||[])];
          const isExpanded = expandedF[f.franchiseId];

          return (
            <div key={f.franchiseId} className="card" style={{ marginBottom:14 }}>
              {/* Header */}
              <div style={{ padding:'12px 16px', background:'rgba(28,25,96,0.03)', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', cursor:'pointer', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}
                onClick={() => setExpandedF(p=>({...p,[f.franchiseId]:!p[f.franchiseId]}))}>
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:'var(--navy)' }}>{f.location || f.franchiseName}</div>
                  {f.location && <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>{f.franchiseName}</div>}
                </div>
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  <span style={{ fontSize:12, color:'var(--text-tertiary)' }}>
                    Remaining: <strong style={{ color:'var(--green)' }}>{fmt(allPeople.reduce((s,p)=>s+(parseFloat(p.totalOwed)||0)-(parseFloat(p.totalTaken)||0),0))}</strong>
                  </span>
                  <i className={`ti ti-chevron-${isExpanded?'up':'down'}`} style={{ fontSize:14, color:'var(--text-tertiary)' }} />
                </div>
              </div>

              {isExpanded && (
                <div style={{ padding:16 }}>
                  {/* People grid */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
                    {allPeople.map((person, pi) => {
                      const isOwner = person.type === 'owner';
                      const color = isOwner ? 'var(--navy)' : 'var(--blue)';
                      const remaining = (parseFloat(person.totalOwed)||0) - (parseFloat(person.totalTaken)||0);

                      return (
                        <div key={pi} className="card" style={{ padding:14, borderLeft:`3px solid ${color}` }}>
                          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                            <div>
                              <div style={{ fontWeight:600, fontSize:13 }}>{person.name}</div>
                              <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:2 }}>
                                {isOwner ? 'Franchisee' : 'Investor'} · {person.sharePercent}%
                              </div>
                            </div>
                            <button className="btn btn-sm btn-primary" style={{ fontSize:11 }} onClick={() => {
                              setWithdrawModal({ franchiseId:f.franchiseId, userId:person.userId, name:person.name, franchiseName:f.franchiseName, email:person.email });
                              setWithdrawForm({ amount:'', method:'Bank Transfer', date:new Date().toISOString().split('T')[0], notes:'' });
                            }}>+ Take Profit</button>
                          </div>

                          <div style={{ display:'grid', gridTemplateColumns: person.openingBalance>0 ? 'repeat(4,1fr)' : 'repeat(3,1fr)', gap:8, marginBottom:10 }}>
                            {person.openingBalance > 0 && (
                              <div style={{ cursor:'pointer' }} onClick={() => setObInfoModal({ name:person.name, amount:person.openingBalance, notes:person.openingBalanceNotes, date:person.openingBalanceDate })}>
                                <div style={{ fontSize:9, color:'var(--amber)', letterSpacing:'0.07em', marginBottom:2 }}>OPENING ℹ</div>
                                <div style={{ fontSize:12, fontWeight:700, color:'var(--amber)' }}>{fmt(person.openingBalance)}</div>
                              </div>
                            )}
                            <div>
                              <div style={{ fontSize:9, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:2 }}>OWED</div>
                              <div style={{ fontSize:12, fontWeight:700, color:'var(--navy)' }}>{fmt(person.totalOwed)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize:9, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:2 }}>TAKEN</div>
                              <div style={{ fontSize:12, fontWeight:600, color:'var(--text-secondary)' }}>{fmt(person.totalTaken)}</div>
                            </div>
                            <div>
                              <div style={{ fontSize:9, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:2 }}>REMAINING</div>
                              <div style={{ fontSize:14, fontWeight:800, color:remaining>=0?'var(--green)':'var(--red)' }}>{fmt(remaining)}</div>
                            </div>
                          </div>

                          <button className="btn btn-sm btn-secondary" style={{ fontSize:11, width:'100%' }} onClick={() => {
                            setOpeningBalanceModal({ franchiseId:f.franchiseId, userId:person.userId, name:person.name, franchiseName:f.franchiseName });
                            setObAmount(person.openingBalance||''); setObNotes(person.openingBalanceNotes||'');
                          }}>Set Opening Balance</button>
                        </div>
                      );
                    })}
                  </div>

                  {/* P&L and History buttons */}
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => setExpandedPL(p=>({...p,[f.franchiseId]:!p[f.franchiseId]}))}>
                      {expandedPL[f.franchiseId] ? 'Hide P&L ▲' : 'View Monthly P&L ▼'}
                    </button>
                    <button className="btn btn-sm btn-secondary" onClick={() => setExpandedHist(h=>({...h,[f.franchiseId]:!h[f.franchiseId]}))}>
                      {expandedHist[f.franchiseId] ? 'Hide History ▲' : 'Profit Taken History ▼'}
                    </button>
                  </div>

                  {/* Monthly P&L */}
                  {expandedPL[f.franchiseId] && (() => {
                    const owners = (f.owners||[]).filter(o=>o.isCurrent!==false);
                    const financials = (f.monthlyPL||f.financials||f.monthlyFinancials||[]).filter(m=>(m.month||'').includes(String(plYear)));
                    const sals = f.salaries||f.ownerSalaries||[];
                    function getSal(email, month) {
                      const applicable = sals.filter(s=>(s.userEmail||s.email||'').toLowerCase()===(email||'').toLowerCase() && parseMonthDate(s.effectiveFrom)<=parseMonthDate(month)).sort((a,b)=>parseMonthDate(b.effectiveFrom)-parseMonthDate(a.effectiveFrom));
                      return applicable.length ? parseFloat(applicable[0].amount)||0 : 0;
                    }
                    if (financials.length === 0) return (
                      <div style={{ marginTop:12, padding:'10px 14px', background:'var(--bg)', borderRadius:'var(--r-md)', fontSize:12, color:'var(--text-tertiary)', textAlign:'center' }}>
                        No P&L data for {plYear}. Enter monthly financials in Branches first.
                      </div>
                    );
                    return (
                      <div style={{ marginTop:12, overflowX:'auto' }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:8, marginBottom:8 }}>
                          <button className="btn btn-sm btn-secondary" style={{ padding:'3px 10px' }} onClick={()=>setPlYear(y=>y-1)}>‹</button>
                          <span style={{ fontSize:13, fontWeight:700, color:'var(--navy)', minWidth:40, textAlign:'center' }}>{plYear}</span>
                          <button className="btn btn-sm btn-secondary" style={{ padding:'3px 10px' }} onClick={()=>setPlYear(y=>y+1)}>›</button>
                        </div>
                        <table className="table">
                          <thead><tr>
                            <th>MONTH</th><th style={{ textAlign:'right' }}>NET PROFIT</th>
                            {owners.map((o,oi)=><th key={oi} style={{ textAlign:'right', color:'var(--green)' }}>{o.name.split(' ')[0]}</th>)}
                          </tr></thead>
                          <tbody>
                            {financials.sort((a,b)=>parseMonthDate(b.month)-parseMonthDate(a.month)).map(m => {
                              const net=(parseFloat(m.income)||0)-(parseFloat(m.rent)||0)-(parseFloat(m.salaries)||0)-(parseFloat(m.royalty)||0)-(parseFloat(m.otherExpenses)||0);
                              const ownerSals=owners.map(o=>getSal(o.email,m.month));
                              const allSals=ownerSals.reduce((s,v)=>s+v,0);
                              return (
                                <tr key={m.month}>
                                  <td style={{ fontSize:12 }}>{m.month}</td>
                                  <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:600, color:net>=0?'var(--blue)':'var(--red)' }}>{fmt(net)}</td>
                                  {owners.map((o,oi)=>{
                                    const oSal=ownerSals[oi];
                                    const share=(net-allSals)*parseFloat(o.sharePercent||0)/100;
                                    const th=oSal+share;
                                    return (
                                      <td key={oi} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>
                                        <div style={{ fontWeight:700, color:th>=0?'var(--green)':'var(--red)' }}>{fmt(th)}</div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                            <tr className="total-row" style={{ background:'var(--blue-bg)', fontWeight:700 }}>
                              <td style={{ fontSize:12 }}>TOTAL {plYear}</td>
                              <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12 }}>{fmt(financials.reduce((s,m)=>s+(parseFloat(m.income)||0)-(parseFloat(m.rent)||0)-(parseFloat(m.salaries)||0)-(parseFloat(m.royalty)||0)-(parseFloat(m.otherExpenses)||0),0))}</td>
                              {owners.map((o,oi)=>{
                                const total=financials.reduce((s,m)=>{
                                  const net=(parseFloat(m.income)||0)-(parseFloat(m.rent)||0)-(parseFloat(m.salaries)||0)-(parseFloat(m.royalty)||0)-(parseFloat(m.otherExpenses)||0);
                                  const ownerSalsTot=owners.map(oo=>getSal(oo.email,m.month));
                                  const allSalsTot=ownerSalsTot.reduce((ss,v)=>ss+v,0);
                                  return s+(net-allSalsTot)*parseFloat(o.sharePercent||0)/100+ownerSalsTot[oi];
                                },0);
                                return <td key={oi} style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontSize:12, fontWeight:700, color:total>=0?'var(--green)':'var(--red)' }}>{fmt(total)}</td>;
                              })}
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}

                  {/* History */}
                  {expandedHist[f.franchiseId] && (
                    <div style={{ marginTop:12 }}>
                      <div style={{ display:'flex', gap:8, marginBottom:10, flexWrap:'wrap' }}>
                        <input placeholder="Search..." value={histSearch} onChange={e=>setHistSearch(e.target.value)} style={{ flex:1, minWidth:140, marginBottom:0 }} />
                        <div style={{ display:'flex', gap:4 }}>
                          <button className="btn btn-sm btn-secondary" style={{ padding:'3px 10px' }} onClick={()=>setHistYear(y=>y<=2025?0:y-1)}>‹</button>
                          <span style={{ fontSize:12, fontWeight:700, color:'var(--navy)', minWidth:40, textAlign:'center', lineHeight:'28px' }}>{histYear===0?'All':histYear}</span>
                          <button className="btn btn-sm btn-secondary" style={{ padding:'3px 10px' }} onClick={()=>setHistYear(y=>y<new Date().getFullYear()?y+1:y)}>›</button>
                        </div>
                      </div>
                      {(() => {
                        const filtered2 = (f.withdrawals||[]).filter(w => {
                          const yr = w.dateTaken ? new Date(w.dateTaken).getFullYear() : null;
                          if (histYear !== 0 && yr !== histYear) return false;
                          if (!histSearch) return true;
                          const q = histSearch.toLowerCase();
                          return (w.userName||'').toLowerCase().includes(q)||(w.method||'').toLowerCase().includes(q)||(w.notes||'').toLowerCase().includes(q);
                        });
                        if (!filtered2.length) return <div style={{ fontSize:12, color:'var(--text-tertiary)' }}>No transactions</div>;
                        return filtered2.map((w,wi)=>(
                          <div key={wi} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
                            <div>
                              <div style={{ fontWeight:600 }}>{w.userName}</div>
                              <div style={{ color:'var(--text-tertiary)' }}>{w.method} · {w.dateTaken?new Date(w.dateTaken).toLocaleDateString('en-GB'):''}</div>
                              {w.notes && <div style={{ color:'var(--text-tertiary)', fontSize:11 }}>{w.notes}</div>}
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                              <span style={{ fontWeight:700, color:'var(--red)' }}>-{fmt(w.amount)}</span>
                              {isAdmin && <>
                                <button className="btn btn-sm btn-secondary" style={{ fontSize:10, padding:'3px 8px' }} onClick={() => {
                                  setEditWithdrawModal(w);
                                  setEditWithdrawForm({ amount:w.amount, method:w.method||'Bank Transfer', date:w.dateTaken?.split('T')[0]||'', notes:w.notes||'' });
                                }}>Edit</button>
                                <button className="btn btn-sm btn-danger" style={{ fontSize:10, padding:'3px 8px' }} onClick={() => deleteWithdrawal(w)}>Del</button>
                              </>}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* All Transactions */}
        <div className="card" style={{ overflow:'hidden' }}>
          <div style={{ padding:'12px 16px', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom: historyOpen?'1px solid var(--border)':undefined }}
            onClick={() => setHistoryOpen(h=>!h)}>
            <div style={{ fontWeight:600, fontSize:13, color:'var(--navy)' }}>ALL TRANSACTIONS</div>
            <i className={`ti ti-chevron-${historyOpen?'up':'down'}`} style={{ fontSize:14, color:'var(--text-tertiary)' }} />
          </div>
          {historyOpen && (
            <div style={{ padding:16 }}>
              <div style={{ marginBottom:12 }}>
                <SearchInput value={historySearch} onChange={setHistorySearch} placeholder="Search by name, franchise or method..." />
              </div>
              {(() => {
                const allW = profitData.reduce((acc,f) => acc.concat((f.withdrawals||[]).map(w=>({...w,franchiseName:f.franchiseName}))), [])
                  .filter(w => {
                    if (!historySearch) return true;
                    const q = historySearch.toLowerCase();
                    return (w.userName||'').toLowerCase().includes(q)||(w.franchiseName||'').toLowerCase().includes(q)||(w.method||'').toLowerCase().includes(q);
                  }).sort((a,b)=>new Date(b.dateTaken)-new Date(a.dateTaken));
                if (!allW.length) return <div style={{ fontSize:13, color:'var(--text-tertiary)' }}>No transactions found</div>;
                return allW.map((w,wi)=>(
                  <div key={wi} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid var(--border)', fontSize:12 }}>
                    <div>
                      <div style={{ fontWeight:600 }}>{w.userName}</div>
                      <div style={{ color:'var(--text-tertiary)' }}>{w.franchiseName} · {w.method} · {w.dateTaken?new Date(w.dateTaken).toLocaleDateString('en-GB'):''}</div>
                      {w.notes && <div style={{ color:'var(--text-tertiary)', fontSize:11 }}>{w.notes}</div>}
                    </div>
                    <span style={{ fontSize:13, fontWeight:700, color:'var(--red)' }}>-{fmt(w.amount)}</span>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Take Profit Modal */}
      {withdrawModal && (
        <Modal title="Take Profit" onClose={() => setWithdrawModal(null)}
          footer={<><button className="btn btn-secondary" onClick={()=>setWithdrawModal(null)}>Cancel</button><button className="btn btn-primary" onClick={saveWithdrawal}>Save</button></>}>
          <div style={{ marginBottom:12, fontSize:12, color:'var(--text-secondary)' }}>{withdrawModal.name} · {withdrawModal.franchiseName}</div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><label>AMOUNT (£) *</label><input type="number" min="0" step="0.01" placeholder="0.00" value={withdrawForm.amount} onChange={e=>setWithdrawForm(f=>({...f,amount:e.target.value}))} /></div>
              <div><label>DATE *</label><input type="date" value={withdrawForm.date} onChange={e=>setWithdrawForm(f=>({...f,date:e.target.value}))} /></div>
            </div>
            <div><label>METHOD</label>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
                {['Bank Transfer','Card','Cash','Cheque'].map(m=>(
                  <button key={m} type="button" onClick={()=>setWithdrawForm(f=>({...f,method:m}))} className="btn btn-sm" style={{ background:withdrawForm.method===m?'var(--navy)':'#fff', color:withdrawForm.method===m?'#fff':'var(--text-secondary)', border:'1px solid var(--border)' }}>{m}</button>
                ))}
              </div>
            </div>
            <div><label>NOTES</label><input placeholder="Optional notes..." value={withdrawForm.notes} onChange={e=>setWithdrawForm(f=>({...f,notes:e.target.value}))} /></div>
          </div>
        </Modal>
      )}

      {/* Edit Withdrawal Modal */}
      {editWithdrawModal && (
        <Modal title="Edit Withdrawal" onClose={()=>setEditWithdrawModal(null)}
          footer={<><button className="btn btn-secondary" onClick={()=>setEditWithdrawModal(null)}>Cancel</button><button className="btn btn-primary" onClick={saveEditWithdrawal}>Save</button></>}>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div><label>AMOUNT (£)</label><input type="number" value={editWithdrawForm.amount} onChange={e=>setEditWithdrawForm(f=>({...f,amount:e.target.value}))} /></div>
              <div><label>DATE</label><input type="date" value={editWithdrawForm.date} onChange={e=>setEditWithdrawForm(f=>({...f,date:e.target.value}))} /></div>
            </div>
            <div><label>METHOD</label>
              <select value={editWithdrawForm.method} onChange={e=>setEditWithdrawForm(f=>({...f,method:e.target.value}))}>
                {['Bank Transfer','Card','Cash','Cheque'].map(m=><option key={m}>{m}</option>)}
              </select>
            </div>
            <div><label>NOTES</label><input value={editWithdrawForm.notes} onChange={e=>setEditWithdrawForm(f=>({...f,notes:e.target.value}))} /></div>
          </div>
        </Modal>
      )}

      {/* Opening Balance Modal */}
      {openingBalanceModal && (
        <Modal title={`Opening Balance — ${openingBalanceModal.name}`} onClose={()=>setOpeningBalanceModal(null)}
          footer={<><button className="btn btn-secondary" onClick={()=>setOpeningBalanceModal(null)}>Cancel</button><button className="btn btn-primary" onClick={saveOpeningBalance}>Save</button></>}>
          <p style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:12 }}>{openingBalanceModal.franchiseName}</p>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div><label>AMOUNT (£) *</label><input type="number" value={obAmount} onChange={e=>setObAmount(e.target.value)} placeholder="0.00" /></div>
            <div><label>NOTES</label><input value={obNotes} onChange={e=>setObNotes(e.target.value)} placeholder="Optional notes..." /></div>
          </div>
        </Modal>
      )}

      {/* Opening Balance Info Modal */}
      {obInfoModal && (
        <Modal title="Opening Balance" onClose={()=>setObInfoModal(null)}
          footer={<button className="btn btn-secondary" onClick={()=>setObInfoModal(null)}>Close</button>}>
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ fontSize:13, color:'var(--text-secondary)' }}>{obInfoModal.name}</div>
            <div><label>AMOUNT</label><div style={{ fontSize:22, fontWeight:800, color:'var(--amber)' }}>{fmt(obInfoModal.amount)}</div></div>
            {obInfoModal.date && <div><label>DATE ADDED</label><div style={{ fontSize:13 }}>{new Date(obInfoModal.date).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})}</div></div>}
            {obInfoModal.notes && <div><label>NOTES</label><div style={{ fontSize:13 }}>{obInfoModal.notes}</div></div>}
          </div>
        </Modal>
      )}
    </div>
  );
}
